import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FaClipboardList, FaFileInvoiceDollar } from 'react-icons/fa';
import { FiFileText, FiSearch, FiUploadCloud } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { dataIsoParaBr } from '../../helpers/dataBr';
import { pdfParaImagem } from '../../helpers/pdfParaImagem';
import {
  EXTENSOES_CANHOTO,
  FilialCanhoto,
  NotaParaCanhoto,
  TAMANHO_MAXIMO_CANHOTO,
  anexarCanhotoManual,
  filtrarNotas,
  listarFiliaisCanhoto,
  listarNotasDaFilial,
} from '../../service/entregas';

// A lista é para escolher, não para navegar: quem procura digita a nota.
const LIMITE_RESULTADOS = 50;

const ACEITA_ARQUIVO = [
  ...EXTENSOES_CANHOTO.map((extensao) => `.${extensao}`),
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
].join(',');

const extensaoDe = (arquivo: File) => (arquivo.name.split('.').pop() || '').toLowerCase();

const tamanhoLegivel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const Rotulo = ({ children }: { children: string }) => (
  <Text fontSize="10px" color="gray.400" letterSpacing="wider" mb={1.5}>
    {children}
  </Text>
);

/** Linha da nota — NF, pedido e hospital, no mesmo formato dos cards do Mapa Cirúrgico. */
const ResumoNota = ({ nota }: { nota: NotaParaCanhoto }) => (
  <Box minW={0} flex={1}>
    <HStack spacing={4}>
      <HStack spacing={2}>
        <Icon as={FaFileInvoiceDollar} color="#68D391" w={3} h={3} />
        <Text fontSize="sm" fontWeight="semibold">
          NF {nota.notaErp}
        </Text>
      </HStack>
      {nota.pedidoErp && (
        <HStack spacing={2}>
          <Icon as={FaClipboardList} color="#63B3ED" w={3} h={3} />
          <Text fontSize="xs" color="gray.300">
            {nota.pedidoErp}
          </Text>
        </HStack>
      )}
    </HStack>
    <Text fontSize="sm" color="gray.200" mt={1} isTruncated>
      {nota.cliente || 'Hospital não informado'}
    </Text>
    {nota.dataEmissao && (
      <Text fontSize="11px" color="gray.500">
        Emissão {dataIsoParaBr(nota.dataEmissao)}
      </Text>
    )}
  </Box>
);

interface Props {
  empresaIdsPermitidas: string[] | null;
  /** Pedidos que já têm canhoto — aparecem na busca, mas não podem ser escolhidos. */
  pedidosComCanhoto: Set<string>;
  onClose: () => void;
  onAnexado: () => void;
}

/**
 * Anexo do canhoto pelo escritório, sem o app do motorista.
 *
 * Enquanto a rastreabilidade com motorista não está em uso, a logística recebe
 * o canhoto assinado (foto ou PDF) e anexa aqui: escolhe a filial, acha a nota
 * pelo número e envia o arquivo. Usa as mesmas rotas do app do motorista (ver
 * anexarCanhotoManual), e o canhoto cai na lista como ENTREGUE, com quem
 * anexou no lugar do motorista.
 */
export const CanhotoManualModal = ({ empresaIdsPermitidas, pedidosComCanhoto, onClose, onAnexado }: Props) => {
  const { user } = useAuth();
  const empresaDaSessao = user?.empresa?.id;
  const toast = useToast();

  const [filiais, setFiliais] = useState<FilialCanhoto[]>([]);
  const [carregandoFiliais, setCarregandoFiliais] = useState(true);
  const [empresaId, setEmpresaId] = useState('');

  const [notas, setNotas] = useState<NotaParaCanhoto[]>([]);
  const [carregandoNotas, setCarregandoNotas] = useState(false);
  const [erroNotas, setErroNotas] = useState('');
  // O GET traz todos os pedidos da filial (é o mesmo endpoint do Mapa), então
  // trocar de filial e voltar não deve baixar tudo de novo.
  const notasPorFilial = useRef<Record<string, NotaParaCanhoto[]>>({});

  const [busca, setBusca] = useState('');
  const [notaEscolhida, setNotaEscolhida] = useState<NotaParaCanhoto | null>(null);

  // `arquivo` é o que a pessoa escolheu; `imagem` é o que sobe — igual ao
  // arquivo quando é foto, a conversão dele quando é PDF (ver pdfParaImagem).
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [imagem, setImagem] = useState<File | null>(null);
  const [convertendo, setConvertendo] = useState(false);
  // Descarta a conversão de um PDF que já foi trocado por outro arquivo.
  const escolhaAtual = useRef(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [erroArquivo, setErroArquivo] = useState('');
  const [arrastando, setArrastando] = useState(false);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let ativo = true;
    listarFiliaisCanhoto(empresaIdsPermitidas)
      .then((lista) => {
        if (!ativo) return;
        setFiliais(lista);
        // Já abre na filial de quem está logado (ou na única que ele tem).
        const daSessao = lista.find((filial) => filial.empresaId === empresaDaSessao);
        if (daSessao) setEmpresaId(daSessao.empresaId);
        else if (lista.length === 1) setEmpresaId(lista[0].empresaId);
      })
      .catch(() => {
        if (ativo) setFiliais([]);
      })
      .finally(() => {
        if (ativo) setCarregandoFiliais(false);
      });
    return () => {
      ativo = false;
    };
  }, [empresaIdsPermitidas, empresaDaSessao]);

  useEffect(() => {
    setNotaEscolhida(null);
    setBusca('');
    setErroNotas('');
    if (!empresaId) {
      setNotas([]);
      return;
    }

    const emCache = notasPorFilial.current[empresaId];
    if (emCache) {
      setNotas(emCache);
      return;
    }

    let ativo = true;
    setCarregandoNotas(true);
    setNotas([]);
    listarNotasDaFilial(empresaId)
      .then((lista) => {
        notasPorFilial.current[empresaId] = lista;
        if (ativo) setNotas(lista);
      })
      .catch(() => {
        if (ativo) setErroNotas('Não foi possível carregar as notas desta filial.');
      })
      .finally(() => {
        if (ativo) setCarregandoNotas(false);
      });
    return () => {
      ativo = false;
    };
  }, [empresaId]);

  // A prévia é a imagem que vai subir (no PDF, já convertida), como object URL
  // do arquivo local; revoga ao trocar ou fechar.
  useEffect(() => {
    if (!imagem) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imagem);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imagem]);

  const encontradas = useMemo(() => filtrarNotas(notas, busca), [notas, busca]);

  const escolherArquivo = async (escolhido?: File | null) => {
    if (!escolhido) return;
    const extensao = extensaoDe(escolhido);
    if (!EXTENSOES_CANHOTO.includes(extensao)) {
      setErroArquivo('Formato não suportado. Envie uma foto (JPG, PNG, WEBP) ou um PDF.');
      return;
    }

    const escolha = ++escolhaAtual.current;
    setErroArquivo('');
    setArquivo(escolhido);
    setImagem(null);

    let paraEnviar = escolhido;
    if (extensao === 'pdf') {
      setConvertendo(true);
      try {
        paraEnviar = await pdfParaImagem(escolhido);
      } catch (erro: any) {
        if (escolha !== escolhaAtual.current) return;
        setArquivo(null);
        setErroArquivo(erro?.message?.startsWith('O PDF tem') ? erro.message : 'Não foi possível ler este PDF.');
        return;
      } finally {
        if (escolha === escolhaAtual.current) setConvertendo(false);
      }
      if (escolha !== escolhaAtual.current) return;
    }

    // O limite vale para o que sobe: no PDF, a imagem gerada.
    if (paraEnviar.size > TAMANHO_MAXIMO_CANHOTO) {
      setArquivo(null);
      setErroArquivo('O arquivo passa de 10 MB, o limite do servidor.');
      return;
    }
    setImagem(paraEnviar);
  };

  const enviar = async () => {
    if (!notaEscolhida || !imagem) return;
    setEnviando(true);
    try {
      await anexarCanhotoManual(notaEscolhida, imagem);
      toast({
        title: 'Canhoto anexado',
        description: `NF ${notaEscolhida.notaErp} — ${notaEscolhida.cliente || 'hospital não informado'}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      onAnexado();
    } catch (erro: any) {
      toast({
        title: 'Não foi possível anexar o canhoto',
        description: erro?.response?.data?.message || 'Tente novamente daqui alguns minutos.',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setEnviando(false);
    }
  };

  const ehPdf = arquivo ? extensaoDe(arquivo) === 'pdf' : false;

  return (
    <Modal isOpen onClose={onClose} size="xl" scrollBehavior="inside" isCentered closeOnOverlayClick={!enviando}>
      <ModalOverlay />
      <ModalContent bg="gray.900" color="gray.50" borderRadius="xl" overflow="hidden">
        <ModalHeader px={{ base: 5, md: 6 }} pt={5} pb={3}>
          <Text fontSize="xs" color="gray.400" letterSpacing="wider">
            CANHOTO MANUAL
          </Text>
          <Text mt={1} pr={10} fontSize="lg" lineHeight="shorter">
            Anexar canhoto de uma nota
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={enviando} />

        <ModalBody px={{ base: 5, md: 6 }} pb={2}>
          <Stack spacing={5}>
            <Box>
              <Rotulo>FILIAL</Rotulo>
              <Select
                size="sm"
                borderRadius="md"
                placeholder={carregandoFiliais ? 'Carregando filiais...' : 'Escolha a filial'}
                isDisabled={carregandoFiliais || enviando}
                value={empresaId}
                onChange={(evento) => setEmpresaId(evento.target.value)}
              >
                {filiais.map((filial) => (
                  <option key={filial.empresaId} value={filial.empresaId}>
                    {filial.codigo ? `${filial.codigo} — ${filial.nome}` : filial.nome}
                  </option>
                ))}
              </Select>
              {!carregandoFiliais && filiais.length === 0 && (
                <Text mt={2} fontSize="xs" color="red.300">
                  Nenhuma filial disponível para o seu usuário.
                </Text>
              )}
            </Box>

            <Box>
              <Rotulo>NOTA FISCAL</Rotulo>
              {notaEscolhida ? (
                <Flex
                  align="center"
                  bg="gray.800"
                  borderWidth="1px"
                  borderColor="orange.300"
                  borderRadius="md"
                  px={3}
                  py={2.5}
                >
                  <ResumoNota nota={notaEscolhida} />
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="orange"
                    ml={3}
                    isDisabled={enviando}
                    onClick={() => setNotaEscolhida(null)}
                  >
                    Trocar
                  </Button>
                </Flex>
              ) : (
                <>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.500" />
                    </InputLeftElement>
                    <Input
                      borderRadius="md"
                      inputMode="numeric"
                      placeholder={empresaId ? 'Digite o número da nota' : 'Escolha a filial primeiro'}
                      isDisabled={!empresaId || carregandoNotas}
                      value={busca}
                      onChange={(evento) => setBusca(evento.target.value)}
                    />
                  </InputGroup>

                  {empresaId && (
                    <Box
                      mt={2}
                      maxH="260px"
                      overflowY="auto"
                      borderWidth="1px"
                      borderColor="gray.700"
                      borderRadius="md"
                    >
                      {carregandoNotas ? (
                        <Flex align="center" justify="center" py={6}>
                          <Spinner color="orange.200" size="sm" />
                        </Flex>
                      ) : erroNotas ? (
                        <Text fontSize="sm" color="red.300" p={3}>
                          {erroNotas}
                        </Text>
                      ) : encontradas.length === 0 ? (
                        <Text fontSize="sm" color="gray.400" p={3}>
                          {busca.trim()
                            ? 'Nenhuma nota encontrada nesta filial.'
                            : 'Esta filial ainda não tem notas emitidas.'}
                        </Text>
                      ) : (
                        <>
                          {encontradas.slice(0, LIMITE_RESULTADOS).map((nota) => {
                            const jaTemCanhoto = pedidosComCanhoto.has(nota.pedidoId);
                            return (
                              <Flex
                                key={nota.pedidoId}
                                as="button"
                                type="button"
                                w="100%"
                                textAlign="left"
                                align="center"
                                px={3}
                                py={2}
                                borderBottomWidth="1px"
                                borderColor="gray.800"
                                opacity={jaTemCanhoto ? 0.5 : 1}
                                cursor={jaTemCanhoto ? 'not-allowed' : 'pointer'}
                                _hover={jaTemCanhoto ? undefined : { bg: 'gray.800' }}
                                disabled={jaTemCanhoto}
                                onClick={() => setNotaEscolhida(nota)}
                              >
                                <ResumoNota nota={nota} />
                                {jaTemCanhoto && (
                                  <Badge colorScheme="green" fontSize="9px" ml={3} flexShrink={0}>
                                    já tem canhoto
                                  </Badge>
                                )}
                              </Flex>
                            );
                          })}
                          {encontradas.length > LIMITE_RESULTADOS && (
                            <Text fontSize="xs" color="gray.500" px={3} py={2}>
                              Mostrando {LIMITE_RESULTADOS} de {encontradas.length} notas — digite o número para
                              encontrar a sua.
                            </Text>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Box>
              <Rotulo>CANHOTO ASSINADO</Rotulo>
              <input
                ref={inputArquivo}
                type="file"
                accept={ACEITA_ARQUIVO}
                style={{ display: 'none' }}
                onChange={(evento) => {
                  escolherArquivo(evento.target.files?.[0]);
                  // Limpa para permitir escolher o mesmo arquivo de novo após um erro.
                  evento.target.value = '';
                }}
              />
              <Flex
                direction="column"
                align="center"
                justify="center"
                minH="150px"
                p={4}
                bg={arrastando ? 'gray.700' : 'gray.800'}
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={arrastando ? 'orange.300' : 'gray.600'}
                borderRadius="md"
                cursor={enviando || convertendo ? 'default' : 'pointer'}
                onClick={() => !enviando && !convertendo && inputArquivo.current?.click()}
                onDragOver={(evento) => {
                  evento.preventDefault();
                  if (!enviando && !convertendo) setArrastando(true);
                }}
                onDragLeave={() => setArrastando(false)}
                onDrop={(evento) => {
                  evento.preventDefault();
                  setArrastando(false);
                  if (!enviando && !convertendo) escolherArquivo(evento.dataTransfer.files?.[0]);
                }}
              >
                {convertendo ? (
                  <>
                    <Spinner color="orange.200" />
                    <Text mt={2} fontSize="sm" color="gray.300">
                      Convertendo o PDF em imagem...
                    </Text>
                  </>
                ) : arquivo ? (
                  <>
                    {previewUrl && (
                      <Image src={previewUrl} alt="Prévia do canhoto" maxH="180px" borderRadius="md" />
                    )}
                    <HStack mt={2} spacing={2} maxW="100%">
                      {ehPdf && <Icon as={FiFileText} color="red.300" flexShrink={0} />}
                      <Text fontSize="sm" fontWeight="medium" textAlign="center" wordBreak="break-all">
                        {arquivo.name}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.400" textAlign="center">
                      {ehPdf ? 'PDF convertido em imagem para envio' : tamanhoLegivel(arquivo.size)} · clique para
                      trocar
                    </Text>
                  </>
                ) : (
                  <>
                    <Icon as={FiUploadCloud} w={8} h={8} color="gray.400" />
                    <Text mt={2} fontSize="sm" textAlign="center">
                      Clique para escolher ou arraste o arquivo aqui
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Foto (JPG, PNG, WEBP) ou PDF, até 10 MB
                    </Text>
                  </>
                )}
              </Flex>
              {erroArquivo && (
                <Text mt={2} fontSize="xs" color="red.300">
                  {erroArquivo}
                </Text>
              )}
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter px={{ base: 5, md: 6 }} py={4}>
          <HStack spacing={3}>
            <Button size="sm" variant="ghost" onClick={onClose} isDisabled={enviando}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="orange"
              isDisabled={!notaEscolhida || !imagem || convertendo}
              isLoading={enviando}
              loadingText="Enviando"
              onClick={enviar}
            >
              Anexar canhoto
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
